import React from 'react';

export const DEFAULT_SIDER_WIDTH = 208;
export const MIN_SIDER_WIDTH = 180;
export const MAX_SIDER_WIDTH = 260;
export const SIDER_WIDTH_STORAGE_KEY = 'easy-record:sider-width:v2';
const clampSiderWidth = (width: number) =>
  Math.round(Math.min(MAX_SIDER_WIDTH, Math.max(MIN_SIDER_WIDTH, width)));
export const getStoredSiderWidth = () => {
  try {
    const raw = window.localStorage.getItem(SIDER_WIDTH_STORAGE_KEY);
    const width = raw?.trim() ? Number(raw) : NaN;
    return Number.isFinite(width)
      ? clampSiderWidth(width)
      : DEFAULT_SIDER_WIDTH;
  } catch {
    return DEFAULT_SIDER_WIDTH;
  }
};
const persistWidth = (width: number) => {
  try {
    window.localStorage.setItem(SIDER_WIDTH_STORAGE_KEY, String(width));
  } catch {
    /* Storage may be disabled. */
  }
};

const HEADER_HEIGHT = 44;
const COLLAPSE_TRIGGER_SAFE_GAP = 44;
const RESIZE_HANDLE_HITBOX = 12;
const COLLAPSED_SIDER_THRESHOLD = 90;

type SiderResizeHandleProps = {
  enabled: boolean;
  routePath: string;
  siderWidth: number;
  onResizeWidth: (width: number) => void;
};

/**
 * Local implementation to keep the resize hit area below the collapse trigger.
 * The shared component currently reserves this gap at the bottom instead.
 */
const SiderResizeHandle: React.FC<SiderResizeHandleProps> = ({
  enabled,
  routePath,
  siderWidth,
  onResizeWidth,
}) => {
  const [dragging, setDragging] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [hovering, setHovering] = React.useState(false);
  const [siderBoundaryX, setSiderBoundaryX] = React.useState(-1);
  const [resizeStartY, setResizeStartY] = React.useState(
    HEADER_HEIGHT + COLLAPSE_TRIGGER_SAFE_GAP,
  );
  const dragStartXRef = React.useRef(0);
  const dragStartWidthRef = React.useRef(siderWidth);
  const activePointerIdRef = React.useRef<number | null>(null);
  const rafIdRef = React.useRef<number | null>(null);
  const pendingWidthRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    const sider = document.querySelector(
      '.ant-pro-sider',
    ) as HTMLElement | null;
    if (!sider) {
      setSiderBoundaryX(-1);
      return;
    }

    const syncPosition = () => {
      const siderRect = sider.getBoundingClientRect();
      setSiderBoundaryX(
        window.innerWidth <= 768 || siderRect.width <= COLLAPSED_SIDER_THRESHOLD
          ? -1
          : siderRect.right,
      );

      const collapseTrigger = document.querySelector<HTMLElement>(
        '.ant-pro-sider-collapsed-button, .ant-layout-sider-trigger',
      );
      const minimumStartY = HEADER_HEIGHT + COLLAPSE_TRIGGER_SAFE_GAP;
      const triggerBottom =
        collapseTrigger?.getBoundingClientRect().bottom ?? 0;
      setResizeStartY(Math.max(minimumStartY, Math.ceil(triggerBottom + 12)));
    };

    syncPosition();
    window.addEventListener('resize', syncPosition);
    const resizeObserver =
      'ResizeObserver' in window ? new ResizeObserver(syncPosition) : null;
    resizeObserver?.observe(sider);

    return () => {
      window.removeEventListener('resize', syncPosition);
      resizeObserver?.disconnect();
    };
  }, [enabled, siderWidth, routePath]);

  React.useEffect(() => {
    if (!dragging || typeof window === 'undefined') {
      return;
    }

    const scheduleResize = (nextWidth: number) => {
      pendingWidthRef.current = clampSiderWidth(nextWidth);
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = window.requestAnimationFrame(() => {
        rafIdRef.current = null;
        const width = pendingWidthRef.current ?? clampSiderWidth(nextWidth);
        pendingWidthRef.current = null;
        persistWidth(width);
        onResizeWidth(width);
      });
    };

    const stopDragging = (event?: PointerEvent | Event) => {
      if (
        event &&
        'pointerId' in event &&
        event.pointerId !== activePointerIdRef.current
      )
        return;
      if (pendingWidthRef.current !== null) {
        persistWidth(pendingWidthRef.current);
        onResizeWidth(pendingWidthRef.current);
        pendingWidthRef.current = null;
      }
      activePointerIdRef.current = null;
      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      setDragging(false);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (activePointerIdRef.current !== event.pointerId) return;
      scheduleResize(
        dragStartWidthRef.current + (event.clientX - dragStartXRef.current),
      );
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stopDragging);
    window.addEventListener('pointercancel', stopDragging);
    window.addEventListener('blur', stopDragging);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopDragging);
      window.removeEventListener('pointercancel', stopDragging);
      window.removeEventListener('blur', stopDragging);
      if (rafIdRef.current !== null)
        window.cancelAnimationFrame(rafIdRef.current);
      pendingWidthRef.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [dragging, onResizeWidth]);

  if (!enabled || siderBoundaryX <= 0) return null;

  const handleStyle: React.CSSProperties = {
    position: 'fixed',
    top: resizeStartY,
    left: siderBoundaryX - RESIZE_HANDLE_HITBOX / 2,
    width: RESIZE_HANDLE_HITBOX,
    height: `calc(100dvh - ${resizeStartY}px)`,
    zIndex: 200,
    display: 'flex',
    justifyContent: 'center',
    cursor: 'col-resize',
    touchAction: 'none',
  };

  return (
    <>
      {dragging ? (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 199,
            cursor: 'col-resize',
          }}
        />
      ) : null}
      {/* biome-ignore lint/a11y/useSemanticElements: Interactive ARIA separator contains resize feedback; a void hr cannot contain it. */}
      <div
        role="separator"
        tabIndex={0}
        aria-orientation="vertical"
        aria-label="调整侧栏宽度"
        aria-valuemin={MIN_SIDER_WIDTH}
        aria-valuemax={MAX_SIDER_WIDTH}
        aria-valuenow={Math.round(siderWidth)}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.stopPropagation();
          activePointerIdRef.current = event.pointerId;
          dragStartXRef.current = event.clientX;
          dragStartWidthRef.current = siderWidth;
          event.currentTarget.setPointerCapture?.(event.pointerId);
          setDragging(true);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const delta = event.shiftKey ? 24 : 8;
          const width = clampSiderWidth(
            siderWidth + (event.key === 'ArrowRight' ? delta : -delta),
          );
          persistWidth(width);
          onResizeWidth(width);
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        style={handleStyle}
      >
        <div
          style={{
            position: 'fixed',
            top: HEADER_HEIGHT,
            bottom: 0,
            left: siderBoundaryX - 0.5,
            width: 1,
            background:
              dragging || hovering || focused
                ? 'var(--app-border)'
                : 'transparent',
            pointerEvents: 'none',
          }}
        />
      </div>
    </>
  );
};

export default SiderResizeHandle;
